import { useRef, useState } from "react";
import {
  AlertTriangle, Bug, CheckCircle2, ClipboardCopy, LoaderCircle,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  collectAuthoritativeClientSnapshot,
  isAuthoritativeSupportReference,
} from "../../hooks/useSupportDiagnostics";

const _dedupeId = () => {
  try { return crypto.randomUUID(); }
  catch { return `voice-diag-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
};

export default function LeviVoiceSupportActions() {
  const [includePrivateContent, setIncludePrivateContent] = useState(false);
  const [copyState, setCopyState] = useState("idle");
  const [reportState, setReportState] = useState("idle");
  const [receipt, setReceipt] = useState("");
  const [error, setError] = useState("");
  const dedupeRef = useRef(_dedupeId());

  const buildSnapshot = () => collectAuthoritativeClientSnapshot({
    includePrivateContent,
  });

  const copyDiagnostics = async () => {
    setCopyState("copying");
    setError("");
    try {
      const text = JSON.stringify(await buildSnapshot(), null, 2);
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else window.prompt("Copy diagnostics:", text);
      setCopyState("copied");
    } catch {
      setCopyState("idle");
      setError("Diagnostics could not be copied. Nothing was submitted.");
    }
  };

  const reportProblem = async () => {
    setReportState("submitting");
    setReceipt("");
    setError("");
    try {
      const snapshot = await buildSnapshot();
      const response = await api.post("/support/diagnostics", {
        client_dedupe_id: dedupeRef.current,
        contractor_note: "Levi voice playback or turn-boundary issue.",
        snapshot,
      });
      const reference = response?.data?.reference || "";
      if (!isAuthoritativeSupportReference(reference)) {
        throw new Error("missing_authoritative_receipt");
      }
      setReceipt(reference);
      setReportState("submitted");
      dedupeRef.current = _dedupeId();
    } catch {
      setReportState("failed");
      setError("The report was not submitted. Please try again.");
    }
  };

  return (
    <section
      className="border-b border-slate-200 bg-slate-50 px-4 py-3"
      aria-label="Levi voice diagnostics"
      data-testid="levi-voice-support-actions"
    >
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copyDiagnostics}
          disabled={copyState === "copying"}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-100 disabled:opacity-60"
          data-testid="levi-voice-diag-copy-btn"
        >
          {copyState === "copying" ? <LoaderCircle size={16} className="animate-spin" /> : <ClipboardCopy size={16} />}
          {copyState === "copied" ? "Diagnostics Copied" : "Copy Diagnostics"}
        </button>
        <button
          type="button"
          onClick={reportProblem}
          disabled={reportState === "submitting"}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-60"
          data-testid="levi-voice-diag-report-btn"
        >
          {reportState === "submitting" ? <LoaderCircle size={16} className="animate-spin" /> : <Bug size={16} />}
          {reportState === "submitting" ? "Submitting…" : "Report This Problem"}
        </button>
      </div>

      <details className="mt-2 text-xs text-slate-600" data-testid="levi-voice-diag-consent-panel">
        <summary className="min-h-11 cursor-pointer select-none py-3 font-semibold text-slate-700">
          Privacy options
        </summary>
        <div className="space-y-3 pb-2">
          <label className="flex min-h-11 items-start gap-3">
            <input
              type="checkbox"
              checked={includePrivateContent}
              onChange={(event) => setIncludePrivateContent(event.target.checked)}
              className="mt-1 h-4 w-4"
              data-testid="levi-voice-diag-consent-transcript"
            />
            <span>Include the latest transcript and reply. Off by default.</span>
          </label>
          <label className="flex min-h-11 items-start gap-3 text-slate-500">
            <input
              type="checkbox"
              checked={false}
              disabled
              readOnly
              className="mt-1 h-4 w-4"
              data-testid="levi-voice-diag-consent-audio"
            />
            <span>Raw audio: not collected, retained, or uploaded.</span>
          </label>
        </div>
      </details>

      {receipt ? (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="levi-voice-diag-case-id">
          <CheckCircle2 size={16} /> Report received: <strong>{receipt}</strong>
        </div>
      ) : null}
      {error ? (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900" role="alert" data-testid="levi-voice-diag-failure-alert">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
        </div>
      ) : null}
    </section>
  );
}